import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// "YYYY-MM-DD" → a long localized date. Built as a LOCAL date: `new Date(iso)`
// is UTC midnight, the previous day anywhere west of Greenwich.
export const formatDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "")
  if (!match) return iso
  const [, year, month, day] = match.map(Number)
  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      dateStyle: "long",
    }).format(new Date(year, month - 1, day))
  } catch {
    return iso // an unknown <html lang> must never block the prompt
  }
}

// Connects to data-controller="pipeline--booking-date-alignment"
//
// v1↔v2 parity (CONEK-1549): when the booking has a linked task whose due date
// (`expected_closed_date`) differs from the event's date, ask the user — BEFORE
// saving — whether to align the task's due date to the event or keep both as-is.
// Mirrors the React form's DateAlignmentModal. The backend interactors already
// apply `booking[task_expected_closed_date]` when present (Create/UpdateBooking),
// so aligning is just a matter of injecting that hidden field before submit.
//
// The task's due date is fetched once, lazily, at submit time (a single GET to
// the task endpoint) — this keeps the shared Ui::EntitySelector untouched. A
// lookup failure never blocks saving.
export default class extends Controller {
  static targets = ["modal", "taskDate", "eventDate"]
  static values = { tasksUrlBase: String }

  connect() {
    // The (taskId|eventDate) pair we've already reconciled — either it matched
    // or the user chose Keep/Align. Re-derived and compared on every submit so a
    // later task/date swap re-arms the prompt. The <form> (and this controller)
    // stays mounted after a failed save, so a one-shot boolean would silently
    // skip the check for a genuinely new mismatch.
    this._clearedFor = null
    // Guards the async gap between preventDefault() and requestSubmit(): a
    // double-click could otherwise start two check() flows and POST twice.
    this._checking = false
  }

  // `submit->` handler on the <form>. Lets the submit through unless a mismatch
  // is found, in which case it defers the real submit until the user chooses.
  async check(event) {
    const taskId = this._taskId()
    if (!taskId) return // no linked task → nothing to reconcile

    const eventDate = this._eventDate()
    if (!eventDate) return // no start date yet → let normal validation run

    const pair = `${taskId}|${eventDate}`
    if (this._clearedFor === pair) return // this exact task+date already reconciled

    // Hold the submit until we know the task's due date.
    event.preventDefault()

    if (this._checking) return // a lookup is already in flight — ignore the re-entry
    this._checking = true
    try {
      const taskDate = await this._fetchTaskDate(taskId)
      if (!taskDate || taskDate === eventDate) {
        this._submit(pair)
        return
      }
      this._fill(taskDate, eventDate)
      this._openModal()
    } finally {
      this._checking = false
    }
  }

  // "Keep current dates" — save without touching the task's due date.
  keep() {
    this._closeModal()
    this._submit(this._currentPair())
  }

  // "Align dates" — set the task's due date to the event date, then save.
  align() {
    this._setHidden("booking[task_expected_closed_date]", this._eventDate())
    this._closeModal()
    this._submit(this._currentPair())
  }

  // --- helpers ---

  _submit(pair) {
    // Remember what we reconciled: requestSubmit() re-fires this submit handler,
    // and the pair guard in check() short-circuits instead of prompting again.
    this._clearedFor = pair
    this.element.requestSubmit()
  }

  _currentPair() {
    return `${this._taskId()}|${this._eventDate()}`
  }

  _taskId() {
    const input = this.element.querySelector('input[name="booking[task_id]"]')
    const value = input && input.value ? input.value.trim() : ""
    return value || null
  }

  // datetime-local value is "YYYY-MM-DDTHH:MM"; the leading 10 chars are the day.
  _eventDate() {
    const input = this.element.querySelector(
      'input[name="booking[start_time]"]',
    )
    const value = input && input.value ? input.value : ""
    return value.length >= 10 ? value.slice(0, 10) : null
  }

  async _fetchTaskDate(taskId) {
    try {
      const response = await get(`${this.tasksUrlBaseValue}/${taskId}`, {
        responseKind: "json",
      })
      if (!response.ok) return null
      const data = await response.json
      const raw = data.expected_closed_date || data.expectedClosedDate
      return raw ? String(raw).slice(0, 10) : null
    } catch {
      return null // a lookup failure must never block saving
    }
  }

  // Shown in the user's locale, like v1's date-fns "PPP"; the raw ISO days stay
  // the logic's (and the hidden field's) values.
  _fill(taskDate, eventDate) {
    if (this.hasTaskDateTarget)
      this.taskDateTarget.textContent = formatDay(taskDate)
    if (this.hasEventDateTarget)
      this.eventDateTarget.textContent = formatDay(eventDate)
  }

  _setHidden(name, value) {
    let input = this.element.querySelector(
      `input[type="hidden"][name="${name}"]`,
    )
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      this.element.appendChild(input)
    }
    input.value = value
  }

  _modalController() {
    if (!this.hasModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
  }

  _openModal() {
    const controller = this._modalController()
    if (controller) controller.open()
  }

  _closeModal() {
    const controller = this._modalController()
    if (controller) controller.close()
  }
}
